import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Compass, ChevronDown, ChevronUp, Undo2 } from 'lucide-react'
import {
  recordReleaseOutcome,
  reopenReleaseOutcome,
  type ReleaseDecision,
  type ReleaseOutcome,
  type TodayReleaseResponse,
} from '../../services/prioritizationService'
import { AskEyebrow } from '../today/AskEyebrow'
import { ORI_KIND_LABEL } from '../../utils/oriAsks'

/**
 * Today's single suggested action from the LifeMap-Weighted
 * Prioritization Engine. Renders ONLY when the engine is enabled,
 * release is live, and there is a concrete item to release — otherwise
 * it stays invisible (the API returns 503 / null when the flags are
 * off), so it is always safe to mount.
 *
 * SUBORDINATE SURFACE (owner report 2026-08-29). The prepared-day CURTAIN
 * on Today is the primary attention selector: it answers "what should I
 * work on" from the prepared plan. This card is a SECOND, independent
 * selector ranking a DIFFERENT candidate pool, so whenever both are up
 * they can name different items on the same screen — two competing
 * answers to one question, which doctrine does not allow.
 *
 * Resolution: this card yields. It renders only when release is live AND
 * it is not duplicating the curtain's job — the early return below
 * (`!decision || !releaseLive || !decision.item_id`) IS that mechanism.
 * Do not add a rendering path that bypasses that return.
 *
 * Defect fix 2026-08-29: the only thing enforcing the yield was
 * `releaseLive` — a deployment flag (PRIORITIZATION_RELEASE_LIVE). Turn it
 * on with a prepared plan up and two engines name two different "one
 * things" on one screen. The subordination is now STRUCTURAL and local:
 * the card reads the SAME prepared-day payload the curtain does (shared,
 * passive, `?build=0`) and stays silent while that plan still has live
 * entries. A prepared-day request that FAILED is not an empty plan, and a
 * plan not built YET is not an empty plan either (the curtain's own call is
 * what builds it) — in both cases the card stays silent, because it cannot
 * prove the curtain is absent, and asking twice is worse than not asking.
 * See curtainIsSilent() for the exact condition.
 *
 * "yanamas maximizes positive life momentum, not productivity."
 */
/** Detail route for a released/held item (fix 2026-08-29: everything used
 * to link to /tasks/<id>, so opening a released habit 404'd). */
function detailPath(itemId: string, itemType?: string | null): string {
  switch ((itemType || '').toLowerCase()) {
    case 'habit':
      return `/habits/${itemId}`
    case 'goal':
      return `/goals/${itemId}`
    case 'skill':
      return `/skills/${itemId}`
    default:
      return `/tasks/${itemId}`
  }
}

/**
 * Presentational since 2026-09-08 — see the note on AdmissionInbox. The page
 * fetches (and applies the yield-to-curtain rule) so the ask zone's count is
 * right before it is opened.
 */
export function TodaysReleaseCard(
  { release, onChanged }: { release: TodayReleaseResponse | null; onChanged: () => void },
) {
  const navigate = useNavigate()
  const [showWhy, setShowWhy] = useState(false)
  const [resolved, setResolved] = useState<ReleaseOutcome | null>(null)
  const decision = release?.decision ?? null
  const releaseLive = !!release?.release_live
  const decisionId =
    (decision as (ReleaseDecision & { decision_id?: number }) | null)?.decision_id ?? null

  if (!decision || !releaseLive || !decision.item_id) return null

  const handleOutcome = async (outcome: ReleaseOutcome) => {
    setResolved(outcome)
    if (decisionId != null) await recordReleaseOutcome(decisionId, outcome)
  }

  // The user already answered today: the decision stays honored across
  // refreshes instead of re-surfacing (owner report 2026-08-24). But a
  // set-aside answer must not VANISH the proposal — the user may find
  // time later and ask for it back (owner report, same day). 'done'
  // stays quietly done; a skip/reschedule renders a one-line bring-back.
  const setAside =
    decision.resolved_outcome === 'skipped' ||
    decision.resolved_outcome === 'rescheduled'
  if (decision.resolved_outcome && !setAside) return null
  if (setAside) {
    return (
      <div className="rounded-3xl border border-soft bg-surface p-5 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <p className="flex-1 min-w-[200px] text-sm text-secondary">
            You set today's move aside
            {decision.resolved_outcome === 'rescheduled' ? ' for another day' : ''}.
            {' '}It's waiting, not lost.
          </p>
          <button
            type="button"
            onClick={async () => {
              if (decisionId == null) return
              const ok = await reopenReleaseOutcome(decisionId)
              if (ok) {
                setResolved(null)
                // The decision is the page's now, so ask it to re-read rather
                // than editing a local copy that no longer exists.
                onChanged()
              }
            }}
            className="min-h-touch inline-flex items-center gap-1.5 rounded-xl border border-brand-subtle-border bg-brand-subtle/60 px-4 text-sm font-medium text-brand hover:bg-brand-subtle transition-colors"
          >
            <Undo2 className="h-4 w-4" />
            I have time after all
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="relative overflow-hidden rounded-2xl bg-spotlight p-5 shadow-spotlight">
      {/* Momentum confetti accent (decorative) */}
      <svg className="pointer-events-none absolute -top-2 right-4 opacity-50" width="120" height="90" viewBox="0 0 120 90" aria-hidden="true">
        <circle cx="14" cy="18" r="3" fill="var(--spotlight-confetti-a)" /><circle cx="52" cy="10" r="2.5" fill="var(--spotlight-confetti-b)" />
        <rect x="86" y="20" width="6" height="6" rx="1" fill="var(--spotlight-confetti-c)" transform="rotate(24 89 23)" />
        <circle cx="104" cy="52" r="3" fill="var(--spotlight-confetti-a)" /><rect x="30" y="46" width="5" height="5" rx="1" fill="var(--spotlight-confetti-b)" transform="rotate(-18 32 48)" />
        <circle cx="70" cy="66" r="2.5" fill="var(--spotlight-confetti-c)" />
      </svg>
      <AskEyebrow icon={<Compass />} label={ORI_KIND_LABEL.offer} typeWord="Today's one move" />

      <p
        className="mt-3 text-lg leading-relaxed text-spotlight-ink font-display cursor-pointer"
        title="Click to open this item"
        onClick={() =>
          decision.item_id &&
          navigate(detailPath(decision.item_id, decision.item_type))
        }
      >
        {/* Typography contract (owner 2026-08-27): the task NAME is what
            the user has to recognize — it must never read smaller or
            quieter than the caring prose around it. Split the copy on the
            title and set the name a step up in size + weight. */}
        {(() => {
          const t = decision.item_title
          if (t && decision.copy.includes(t)) {
            const i = decision.copy.indexOf(t)
            return (
              <>
                <span className="text-spotlight-ink-soft">{decision.copy.slice(0, i)}</span>
                <span className="text-xl font-semibold underline decoration-spotlight-accent/50 decoration-2 underline-offset-4">{t}</span>
                <span className="text-spotlight-ink-soft">{decision.copy.slice(i + t.length)}</span>
              </>
            )
          }
          return decision.copy
        })()}
      </p>

      {decision.starter_step && decision.action === 'shrink' && (
        <p className="mt-2 rounded-lg bg-white/10 px-3 py-2 text-sm text-spotlight-ink">
          First step: {decision.starter_step}
        </p>
      )}

      {decision.requires_confirmation && (
        <p className="mt-2 text-xs italic text-spotlight-ink-soft">
          This is a big one — only if it feels right today.
        </p>
      )}

      {resolved ? (
        <p className="mt-4 text-sm font-medium text-spotlight-accent">
          {resolved === 'done' && 'Wonderful — that is real momentum.'}
          {resolved === 'rescheduled' && 'Good call. We will bring it back when the timing fits.'}
          {resolved === 'skipped' && 'That is okay. Nothing is lost; we can choose again.'}
        </p>
      ) : (
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => handleOutcome('done')}
            className="min-h-touch rounded-xl bg-spotlight-accent px-5 text-sm font-bold text-spotlight-accent-ink shadow-spotlight-accent hover:bg-spotlight-accent-hover transition-colors"
          >
            I did it
          </button>
          <button
            type="button"
            onClick={() => handleOutcome('rescheduled')}
            className="min-h-touch rounded-xl border border-spotlight-border px-4 text-sm text-spotlight-ink-soft hover:bg-white/5 transition-colors"
          >
            Another day
          </button>
          <button
            type="button"
            onClick={() => handleOutcome('skipped')}
            className="min-h-touch rounded-xl border border-spotlight-border px-4 text-sm text-spotlight-ink-soft hover:bg-white/5 transition-colors"
          >
            Not today
          </button>
        </div>
      )}

      {(decision.reasons.length > 0 || decision.not_released.length > 0) && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowWhy((v) => !v)}
            className="flex items-center gap-1 min-h-touch text-xs font-medium text-spotlight-ink-faint hover:text-spotlight-ink-soft hover:underline"
          >
            {showWhy ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            Why this, and not the others?
          </button>
          {showWhy && (
            <div className="mt-2 space-y-2 text-xs text-spotlight-ink-soft">
              {decision.reasons.length > 0 && (
                <ul className="list-disc space-y-1 pl-4">
                  {decision.reasons.map((r, i) => (
                    <li key={`r-${i}`}>{r}</li>
                  ))}
                </ul>
              )}
              {decision.not_released.length > 0 && (
                <div>
                  <p className="font-medium text-spotlight-ink">Kept warm for now:</p>
                  <ul className="list-disc space-y-1 pl-4">
                    {decision.not_released.map((nr) => (
                      <li
                        key={nr.item_id}
                        className="cursor-pointer"
                        title="Click to open this item"
                        onClick={() => navigate(detailPath(nr.item_id, nr.item_type))}
                      >
                        {nr.title ? <span className="font-medium text-spotlight-ink">{nr.title}</span> : null}
                        {nr.title ? ' — ' : ''}{nr.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
